/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  Teleport,
  type CSSProperties,
  type PropType,
} from "vue";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  type Placement,
  type Strategy,
} from "@floating-ui/vue";

export type DropdownSlotProps = {
  open: boolean;
  close: () => void;
  toggle: () => void;
  setOpen: (open: boolean) => void;
};

export const Dropdown = defineComponent({
  name: "Dropdown",
  props: {
    open: {
      type: Boolean as PropType<boolean | undefined>,
      default: undefined,
    },
    defaultOpen: Boolean,
    disabled: Boolean,
    placement: {
      type: String as PropType<Placement>,
      default: "bottom-start",
    },
    strategy: {
      type: String as PropType<Strategy>,
      default: "fixed",
    },
    offset: {
      type: Number,
      default: 8,
    },
    shiftPadding: {
      type: Number,
      default: 8,
    },
    flip: {
      type: Boolean,
      default: true,
    },
    shift: {
      type: Boolean,
      default: true,
    },
    matchReferenceWidth: Boolean,
    teleport: {
      type: [String, Object] as PropType<string | HTMLElement>,
      default: "body",
    },
    triggerOnClick: {
      type: Boolean,
      default: true,
    },
    closeOnOutsideClick: {
      type: Boolean,
      default: true,
    },
    closeOnEscape: {
      type: Boolean,
      default: true,
    },
    closeOnContentClick: Boolean,
    anchorClass: String,
    contentClass: String,
    contentStyle: {
      type: [String, Object] as PropType<string | CSSProperties>,
      default: undefined,
    },
    zIndex: {
      type: Number,
      default: 1200,
    },
    onOpenChange: Function as PropType<(open: boolean) => void>,
  },
  setup(props, { slots }) {
    const internalOpen = ref(props.defaultOpen);
    const referenceEl = ref<HTMLElement | null>(null);
    const floatingEl = ref<HTMLElement | null>(null);
    const isControlled = computed(() => props.open !== undefined);
    const isOpen = computed(() =>
      isControlled.value ? Boolean(props.open) : internalOpen.value,
    );

    const middleware = computed(() => {
      const nextMiddleware = [offset(props.offset)];

      if (props.flip) {
        nextMiddleware.push(flip());
      }
      if (props.shift) {
        nextMiddleware.push(shift({ padding: props.shiftPadding }));
      }
      if (props.matchReferenceWidth) {
        nextMiddleware.push(
          size({
            apply({ rects, elements }) {
              Object.assign(elements.floating.style, {
                minWidth: `${rects.reference.width}px`,
              });
            },
          }),
        );
      }

      return nextMiddleware;
    });

    const { floatingStyles, update } = useFloating(referenceEl, floatingEl, {
      placement: computed(() => props.placement),
      strategy: computed(() => props.strategy),
      whileElementsMounted: autoUpdate,
      middleware,
    });

    const setOpen = (nextOpen: boolean) => {
      if (props.disabled) return;
      if (!isControlled.value) {
        internalOpen.value = nextOpen;
      }
      props.onOpenChange?.(nextOpen);
      if (nextOpen) {
        nextTick(() => update());
      }
    };

    const close = () => setOpen(false);
    const toggle = () => setOpen(!isOpen.value);

    const handleAnchorClick = () => {
      if (props.triggerOnClick) {
        toggle();
      }
    };

    const handleAnchorKeydown = (event: KeyboardEvent) => {
      if (!props.triggerOnClick) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggle();
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (!isOpen.value || !props.closeOnOutsideClick) return;

      const target = event.target as Node | null;
      if (!target) return;
      if (referenceEl.value?.contains(target)) return;
      if (floatingEl.value?.contains(target)) return;

      close();
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (!isOpen.value || !props.closeOnEscape || event.key !== "Escape") {
        return;
      }

      event.stopPropagation();
      close();
    };

    const handleContentClick = () => {
      if (props.closeOnContentClick) {
        close();
      }
    };

    onMounted(() => {
      document.addEventListener("pointerdown", handlePointerDown, {
        capture: true,
      });
      document.addEventListener("keydown", handleKeydown, { capture: true });
    });

    onUnmounted(() => {
      document.removeEventListener("pointerdown", handlePointerDown, {
        capture: true,
      });
      document.removeEventListener("keydown", handleKeydown, { capture: true });
    });

    const slotProps = computed<DropdownSlotProps>(() => ({
      open: isOpen.value,
      close,
      toggle,
      setOpen,
    }));

    return () => (
      <>
        <div
          ref={referenceEl}
          class={["inline-flex", props.anchorClass].filter(Boolean).join(" ")}
          onClick={handleAnchorClick}
          onKeydown={handleAnchorKeydown}
        >
          {slots.trigger?.(slotProps.value)}
        </div>

        {isOpen.value && (
          <Teleport to={props.teleport}>
            <div
              ref={floatingEl}
              class={props.contentClass}
              style={[
                floatingStyles.value,
                { zIndex: props.zIndex },
                props.contentStyle,
              ]}
              onClick={handleContentClick}
            >
              {slots.default?.(slotProps.value)}
            </div>
          </Teleport>
        )}
      </>
    );
  },
});
